import { SettingDrawer } from '@ant-design/pro-components';
import type {
  RequestConfig,
  RequestOptions,
  RunTimeLayoutConfig,
  RuntimeAntdConfig,
} from '@umijs/max';
import { history, useAntdConfigSetter, useModel } from '@umijs/max';
import React from 'react';
import { UserOutlined } from '@ant-design/icons';
import {
  AvatarDropdown,
  ContentToolbar,
  Footer,
  NavThemeHtmlClass,
  SelectLang,
  ThemeToggle,
} from '@/components';
import {
  wrapMenuItemRenderWithDeepIcon,
  wrapSubMenuItemRenderWithNavigation,
} from '@/components';
import {
  createSiderMenuHeaderRender,
  siderMenuContentRender,
} from '@/components/SiderCollapse';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import {
  getLayoutColorPrimaryForNav,
  mergeAntdThemeForNavMode,
} from '../config/antdThemeByMode';
import defaultSettings from '../config/defaultSettings';
import {
  type LayoutSettingsWithToken,
  getProLayoutContentBackgroundStyle,
  mergeProLayoutToken,
} from '../config/proLayoutThemeByMode';
import { errorConfig } from './requestErrorConfig';
import {
  clearTokenBundle,
  getAccessToken,
  loadTokenBundle,
  refreshAccessTokenIfNeeded,
} from './utils/auth';
import { isAuthFreePath, LOGIN_PATH } from './utils/authPath';
import {
  NAV_THEME_STORAGE_KEY,
  resolveNavTheme,
  syncNavThemeHtmlClass,
} from './utils/navThemeHtmlClass';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';

/** 供稳定的 layout.onPageChange 读取，避免每次 layout() 新建函数触发 ProLayout 死循环 */
const layoutAuthRef: {
  currentUser: API.CurrentUser | undefined;
} = {
  currentUser: undefined,
};

function layoutPageChangeHandler() {
  const { pathname } = history.location;
  if (isAuthFreePath(pathname)) return;
  if (getAccessToken()) return;
  if (layoutAuthRef.currentUser) return;
  if (pathname === LOGIN_PATH) return;
  history.push(LOGIN_PATH);
}

function mergeSettingsWithStorage(): LayoutSettingsWithToken {
  const nav = resolveNavTheme();
  return {
    ...(defaultSettings as LayoutSettingsWithToken),
    navTheme: nav,
    /** 与 `mergeAntdThemeForNavMode` 一致，否则 ProLayout 不会走「注入 token.colorPrimary」那条路径 */
    colorPrimary: getLayoutColorPrimaryForNav(nav),
    token: mergeProLayoutToken(
      (defaultSettings as LayoutSettingsWithToken).token,
      nav,
    ),
  };
}

/** 与本地存储一致；浅色 / 深色按钮等见 config/antdThemeByMode.ts */
export const antd: RuntimeAntdConfig = (memo) => ({
  ...memo,
  theme: mergeAntdThemeForNavMode(memo.theme, resolveNavTheme()),
});

/** 开发环境：SettingDrawer 与顶栏主题切换、antd 算法、localStorage 同步 */
const DevSettingDrawer: React.FC = () => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const setAntdConfig = useAntdConfigSetter();

  return (
    <SettingDrawer
      disableUrlParams
      enableDarkTheme
      settings={initialState?.settings}
      onSettingChange={(settings) => {
        const s = settings as LayoutSettingsWithToken;
        const nav = s.navTheme;
        if (nav === 'light' || nav === 'realDark') {
          try {
            localStorage.setItem(NAV_THEME_STORAGE_KEY, nav);
          } catch {
            // ignore
          }
        }
        setAntdConfig((prev) => ({
          ...prev,
          theme: mergeAntdThemeForNavMode(
            prev.theme,
            s.navTheme === 'realDark' ? 'realDark' : 'light',
          ),
        }));
        const navMode =
          s.navTheme === 'realDark' ? 'realDark' : 'light';
        setInitialState((pre) => ({
          ...pre,
          settings: {
            ...s,
            ...(pre?.settings?.navTheme !== s.navTheme
              ? { colorPrimary: getLayoutColorPrimaryForNav(navMode) }
              : {}),
            token: mergeProLayoutToken(s.token, navMode),
          },
        }));
        syncNavThemeHtmlClass(navMode);
      }}
    />
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: LayoutSettingsWithToken;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    if (!getAccessToken()) {
      return undefined;
    }
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      const bundle = loadTokenBundle();
      if (bundle?.accessToken) {
        return { name: 'member', userid: 'member' } as API.CurrentUser;
      }
    }
    return undefined;
  };
  const { location } = history;
  if (!isAuthFreePath(location.pathname)) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: mergeSettingsWithStorage(),
    };
  }
  return {
    fetchUserInfo,
    settings: mergeSettingsWithStorage(),
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const settingsTyped = initialState?.settings as
    | LayoutSettingsWithToken
    | undefined;
  const prevMenuItemRender = settingsTyped?.menuItemRender;
  const prevSubMenuItemRender = settingsTyped?.subMenuItemRender;

  const nav: 'light' | 'realDark' =
    (initialState?.settings?.navTheme ?? defaultSettings.navTheme) === 'realDark'
      ? 'realDark'
      : 'light';

  layoutAuthRef.currentUser = initialState?.currentUser;

  return {
    footerRender: () => <Footer />,
    /** 内容区顶部面包屑（PageContainer/ProLayout）关闭 */
    breadcrumbRender: false,
    onPageChange: layoutPageChangeHandler,
    links: [],
    /** 收起按钮移到 logo 行右侧，底部默认按钮关闭 */
    collapsedButtonRender: false,
    menuHeaderRender: createSiderMenuHeaderRender(),
    menuContentRender: siderMenuContentRender,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      return (
        <>
          <NavThemeHtmlClass />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100%',
              ...getProLayoutContentBackgroundStyle(nav),
            }}
          >
            <ContentToolbar>
              <ThemeToggle key="theme" />
              <SelectLang key="SelectLang" />
              <AvatarDropdown>
                <span
                  style={{
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    borderRadius: 8,
                  }}
                >
                  <UserOutlined style={{ fontSize: 18 }} aria-hidden />
                </span>
              </AvatarDropdown>
            </ContentToolbar>
            <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
          </div>
          {isDev && <DevSettingDrawer />}
        </>
      );
    },
    ...initialState?.settings,
    /**
     * Pro SiderMenu：未传 theme 时 `theme !== 'dark'` 为 true，会误加 `ant-pro-sider-light`，
     * 深色侧栏下收起按钮 hover 会走「浅色」样式（黑字），图标几乎看不见。
     */
    theme: nav === 'realDark' ? 'dark' : 'light',
    contentStyle: {
      ...(initialState?.settings as LayoutSettingsWithToken | undefined)
        ?.contentStyle,
      ...getProLayoutContentBackgroundStyle(nav),
    },
    /**
     * 当前为二级菜单（创作 → 图片/视频），使用 inline 在侧栏内展开；
     * 若启用三级菜单参考 routes.ts 注释块，可改回 vertical + triggerSubMenuAction: 'hover'。
     */
    menuProps: {
      mode: 'inline',
    },
    /** 三级叶子：Pro 内部 hasIcon 为 false，需自定义渲染才能显示 routes.icon */
    menuItemRender: wrapMenuItemRenderWithDeepIcon(
      typeof prevMenuItemRender === 'function' ? prevMenuItemRender : undefined,
    ),
    /** 二级 SubMenu：`icon-xxx` 在无 Symbol 时 Pro 不渲染，与本地 iconfont 对齐 */
    subMenuItemRender: wrapSubMenuItemRenderWithNavigation(
      typeof prevSubMenuItemRender === 'function'
        ? prevSubMenuItemRender
        : undefined,
    ),
    /**
     * 必须放在 `...initialState?.settings` 之后：否则模板/本地里带的装饰图会覆盖为空。
     * 那些 PNG 叠在整页底层，看起来像渐变 + 六边形线稿，与纯色 token 冲突。
     */
    bgLayoutImgList: [],
    /**
     * side 布局下头像与操作默认在侧栏底部；改到内容区顶部后需关闭，避免重复。
     * 见 {@link ContentToolbar} 注释。
     */
    actionsRender: false,
    avatarProps: false,
    /**
     * Umi `plugin-layout` 默认注入 `rightContentRender`（含 SelectLang）。
     * Pro SiderMenu 在 `!actionsDom && rightContentRender` 时仍会渲染侧栏底部 `.ant-pro-sider-actions` 容器，需整体关闭。
     */
    rightContentRender: false,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  timeout: 30000,
  requestInterceptors: [
    async (config: RequestOptions) => {
      const url = String(config?.url ?? '');
      const isOauthTokenRequest = url.includes('/oauth2/token');
      const token = isOauthTokenRequest
        ? null
        : (await refreshAccessTokenIfNeeded()) || getAccessToken();
      const headers: Record<string, string> = {
        ...(config.headers as Record<string, string>),
      };

      const isMultipart = config.data instanceof FormData;
      if (!isOauthTokenRequest && !isMultipart) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      }
      if (token && !isOauthTokenRequest && !headers.Authorization) {
        headers.Authorization = `Bearer ${token}`;
      }

      return {
        ...config,
        headers,
      };
    },
  ],
  responseInterceptors: [
    (response) => {
      const responseUrl = (response as { url?: string })?.url || '';
      const isOauthTokenResponse =
        typeof responseUrl === 'string' && responseUrl.includes('/oauth2/token');
      if (response.status === 401 && !isOauthTokenResponse) {
        clearTokenBundle();
        layoutAuthRef.currentUser = undefined;
        const pathname = window.location.pathname;
        if (!isAuthFreePath(pathname) && pathname !== LOGIN_PATH) {
          history.push(LOGIN_PATH);
        }
      }
      return response;
    },
  ],
};
